import React, { useContext, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { Link, useHistory, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { Gr8piFormattedDate } from '../../../components/Gr8piFormattedDateTime';
import AssessmentsTimeline from '../../../components/assessmentTimeline/Timeline';
import { Loading } from '../../../components/Loading';
import { anonymizeRespondent, updateRespondentTargetJob } from '../../../services/apps/RespondentServices';
import Boundary from '../../../components/ErrorBoundary';
import AssessmentUtil from '../../../utils/AssessmentUtil';
import { AccountContext } from '../../../components/AccountContext';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { growlErrors, growlActionSuccess } from '../../../utils/NotificationUtil';
import RespondentTargetJobDisplay from './respondentTargetJob/RespondentTargetJobDisplay';
import RespondentEditContainer from './edit/RespondentEditContainer';
import RespondentDisplay from './RespondentDisplay';
import TargetJobEditContainer from './edit-job/TargetJobEditContainer';
import RespondentRemoveConfirmForm from './RespondentRemoveConfirmForm';
import { DeleteTextButton } from '../../../components/button/ActionButton';
import useSubscriptionExpiredRestrictions from '../../../hooks/useSubscriptionExpiredRestrictions';

const i18n = defineMessages({
  successNoti: { id: 'noti.success', defaultMessage: 'Success!' },
  respondent: { id: 'respondent', defaultMessage: 'respondent' },
  removeRespondent: { id: 'remove_header.respondent', defaultMessage: 'Remove Respondent' },
  anonymizationWarning: {
    id: 'respondent.delete.warning',
    defaultMessage: 'This respondent will automatically be anonymized on: ',
  },
  anonymizedOn: {
    id: 'respondent.anonymized.warning',
    defaultMessage: 'This respondent was anonymized on: ',
  },
});

const sortByCreatingTime = (asmtFirst, asmtSecond) => {
  const first = moment(asmtFirst.createdOn);
  const second = moment(asmtSecond.createdOn);
  return second.valueOf() - first.valueOf();
};

const RespondentDetail = ({ getRespondentDetail, respondentAsmt }) => {
  const [editFlags, setEditFlags] = useState({
    core: false,
    targetJob: false,
  });
  const [jobTargetUpdating, setJobTargetUpdating] = useState(false);
  const [showRemoveConfirmationModal, setShowRemoveConfirmModal] = useState(false);
  const [hasError, setHasError] = useState(false);

  const history = useHistory();
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { formatMessage, formatDate } = useIntl();
  const { translatedTags } = useSelector((state) => state.i18n);
  const { respondentId } = useParams();
  const { subscriptionIsExpired } = useSubscriptionExpiredRestrictions();

  const anonymizationTemplate = (respondent) => {
    if (respondent.anonymizedOn) {
      return (
        <Button
          disabled
          icon="pi pi-info"
          className="p-button-warning"
          label={
            formatMessage(i18n.anonymizedOn) +
            formatDate(new Date(respondent.anonymizedOn), {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })
          }
        />
      );
    }

    if (respondent.scheduledForAnonymizationOn) {
      return (
        <Button
          disabled
          icon="pi pi-info"
          className="p-button-warning"
          label={
            formatMessage(i18n.anonymizationWarning) +
            formatDate(new Date(respondent.scheduledForAnonymizationOn), {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })
          }
        />
      );
    }

    return (
      <DeleteTextButton
        onClick={() => setShowRemoveConfirmModal(true)}
        position="right"
        styleClass=""
        disabled={subscriptionIsExpired}
        text={formatMessage(i18n.removeRespondent)}
      />
    );
  };

  const updateJobTarget = (values) => {
    setJobTargetUpdating(true);

    const { targetJob, respondentId: respondentIdFromForm } = values;
    const targetJobId = targetJob ? targetJob.id : '';
    updateRespondentTargetJob({
      targetJobId,
      respondentId: respondentIdFromForm,
      accountId: currentAccount,
    })
      .then(() => {
        setJobTargetUpdating(false);
        setEditFlags({
          ...editFlags,
          targetJob: false,
        });
        growlActionSuccess(growl, formatMessage(i18n.successNoti));
      })
      .catch((errors) => {
        setJobTargetUpdating(false);
        setEditFlags({
          ...editFlags,
          targetJob: false,
        });
        growlErrors(growl, errors);
      });
  };

  const toggleEdit = (section) => {
    const updatedEditFlags = { ...editFlags };
    updatedEditFlags[section] = !updatedEditFlags[section];

    setEditFlags(updatedEditFlags);
  };

  const processRemovingRespondent = async (formikValues) => {
    const { requestForDeletionOn, requestForDeletionDateChecked } = formikValues;

    if (requestForDeletionDateChecked && !requestForDeletionOn) {
      growl.show({ severity: 'warn', summary: 'Please select deletion request date', sticky: true });
      return;
    }

    if (respondentId) {
      try {
        await anonymizeRespondent({
          respondentId,
          accountId: currentAccount,
        });
        setShowRemoveConfirmModal(false);

        growlActionSuccess(growl, formatMessage(i18n.successNoti));
        const listURL = `/accounts/${currentAccount}/respondents`;
        history.push(listURL);
      } catch (errors) {
        growlErrors(growl, errors);
        setHasError(true);
      }
    }
  };

  const detailLoading = getRespondentDetail ? getRespondentDetail.loading : false;
  const resAsmtLoading = respondentAsmt ? respondentAsmt.loading : false;
  const loading = detailLoading || resAsmtLoading;

  const { account } = getRespondentDetail;
  if (loading || !account || !account.respondent || !respondentAsmt.account || !respondentAsmt.account.respondent) {
    return <Loading />;
  }

  const { respondent } = account;

  const { timeline } = respondentAsmt.account.respondent;
  const generalAsmt = AssessmentUtil.mapInstrumentToAssessment(timeline).sort(sortByCreatingTime);
  const assessments = AssessmentUtil.getStandardAsmt(timeline);
  const newAsmtURL = `/accounts/${currentAccount}/assessments/new/${respondent.id}`;
  const newSurveyURL = `/accounts/${currentAccount}/surveys/new/${respondent.id}`;
  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <div className="p-grid">
            <div className="p-col-12 align-right">
              <p>
                <FormattedMessage id="attrs.added.verbose" defaultMessage="Added on">
                  {(text) => <span>{text}</span>}
                </FormattedMessage>
                &nbsp;
                <strong>{respondent.createdOn && <Gr8piFormattedDate dateValue={respondent.createdOn} />}</strong>
                &nbsp;
                <FormattedMessage id="by" defaultMessage="by">
                  {(text) => <span>{`${text} `}</span>}
                </FormattedMessage>
                {respondent.createdBy && (
                  <span>{`${respondent.createdBy.firstName} ${respondent.createdBy.lastName}`}</span>
                )}
              </p>
            </div>
            <div className="p-sm-12 p-md-6">
              {editFlags.core ? (
                <RespondentEditContainer
                  respondent={respondent}
                  toggleEdit={(section) => toggleEdit(section)}
                  availableTranslations={translatedTags}
                />
              ) : (
                <RespondentDisplay
                  respondent={respondent}
                  toggleEdit={(section) => toggleEdit(section)}
                  availableTranslations={translatedTags}
                />
              )}
              <br />
              <div className="panel panel-default panel-padded">
                {editFlags.targetJob ? (
                  <Boundary>
                    <TargetJobEditContainer
                      respondent={respondent}
                      isUpdating={jobTargetUpdating}
                      toggleEdit={(section) => toggleEdit(section)}
                      updateTargetJob={(values) => updateJobTarget(values)}
                    />
                  </Boundary>
                ) : (
                  <Boundary>
                    <RespondentTargetJobDisplay
                      respondent={respondent}
                      toggleEdit={(section) => toggleEdit(section)}
                      availableTranslations={translatedTags}
                      currentAccount={currentAccount}
                      assessments={assessments}
                    />
                  </Boundary>
                )}
              </div>
            </div>
            <div className="p-sm-12 p-md-6">
              <Panel>
                <div className="p-grid">
                  <div className="p-col-12">{anonymizationTemplate(respondent, formatMessage, formatDate)}</div>
                  {!respondent.anonymizedOn && (
                    <div className="p-col-12">
                      <Link to={newAsmtURL}>
                        <FormattedMessage id="assessment.new" defaultMessage="New Assessment">
                          {(text) => <Button label={text} icon="fa fa-plus-circle" />}
                        </FormattedMessage>
                      </Link>
                      <Link to={newSurveyURL}>
                        <FormattedMessage id="360.new.short" defaultMessage="New Survey">
                          {(text) => <Button label={text} icon="fa fa-plus-circle" />}
                        </FormattedMessage>
                      </Link>
                    </div>
                  )}
                </div>
                <AssessmentsTimeline
                  currentAccount={currentAccount}
                  respondent={respondent}
                  assessments={generalAsmt}
                  surveyError={hasError}
                  availableTranslations={translatedTags}
                />
              </Panel>
            </div>
          </div>
        </div>
      </div>
      <RespondentRemoveConfirmForm
        isVisible={showRemoveConfirmationModal}
        cancelRemove={() => setShowRemoveConfirmModal(false)}
        confirmRemove={(values) => processRemovingRespondent(values)}
      />
    </div>
  );
};

RespondentDetail.propTypes = {
  getRespondentDetail: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    respondent: PropTypes.object,
    user: PropTypes.object,
    error: PropTypes.object,
    account: PropTypes.shape({
      respondent: PropTypes.shape({
        id: PropTypes.string.isRequired,
        anonymizedOn: PropTypes.objectOf(PropTypes.any).isRequired,
        createdOn: PropTypes.objectOf(PropTypes.any).isRequired,
        createdBy: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  respondentAsmt: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default RespondentDetail;
